import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { getCategories, getSubCategories } from "../../../api/taxonomy";
import { X, Plus, RotateCcw } from "lucide-react";
import { useToast } from "../../../context/ToastContext";
import ProfessionalRegistrationLayout from "./ProfessionalRegistrationLayout";
import { MediaUploadService } from "../../../services/MediaUploadService";
import CategoryPickerPopup from "../../common/CategoryPickerPopup";

const predefinedLanguages = ["English", "Hindi", "Bengali"];

function Spinner() {
  return (
    <svg className="animate-spin w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}

export default function PlannerExpertise({ onPrev, onNext, initialData = {} }) {
  const getProfession = (data) => {
    const raw = data.professionalRole || data.purpose || "";
    if (raw === "planner") return "planner";
    if (raw === "performer" || raw === "artist") return "performer";
    if (raw === "crew" || raw === "volunteer") return "crew";
    return "planner";
  };
  const profession = useRef(getProfession(initialData));

  const [languages, setLanguages] = useState(initialData.languages || []);
  const [addingLanguage, setAddingLanguage] = useState(false);
  const [newLanguage, setNewLanguage] = useState("");

  const [categoryOptions, setCategoryOptions] = useState([]);

  const [selectedCategories, setSelectedCategories] = useState(initialData.rawCategories || initialData.categories || []);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const categoryWrapperRef = useRef(null);

  const [portfolioImages, setPortfolioImages] = useState(
    (initialData.portfolioMedia || []).map((m) => ({
      id: m.media + "_restored",
      file: null,
      preview: m.url || "",
      isVid: m.isVid || (m.url ? !!m.url.match(/\.(mp4|mov|quicktime|webm)(\?.*)?$/i) : false),
      status: "uploaded",
      progress: 100,
      mediaId: m.media,
    }))
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const { showError } = useToast();

  const MAX_IMAGES = 10;
  const MIN_IMAGES = 5;
  const ALLOWED_FORMATS = [
    "image/jpeg", "image/png", "image/jpg",
    "video/mp4", "video/mov", "video/quicktime", "video/webm",
  ];

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const catData = await getCategories("common");
        if (catData.success) setCategoryOptions(catData.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCats();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryWrapperRef.current && !categoryWrapperRef.current.contains(event.target)) {
        setIsPopupOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);





  const uploadFile = useCallback(async (entryId, file) => {
    try {
      const media = await MediaUploadService.uploadMedia(
        file,
        { folder: "portfolio", tags: ["registration_temp"] },
        (progress) => {
          setPortfolioImages((prev) =>
            prev.map((img) => img.id === entryId ? { ...img, progress } : img)
          );
        }
      );
      setPortfolioImages((prev) => {
        const stillExists = prev.some((img) => img.id === entryId);
        if (!stillExists) {
          MediaUploadService.deleteMedia(media._id);
          return prev;
        }
        return prev.map((img) =>
          img.id === entryId
            ? { ...img, status: "uploaded", progress: 100, mediaId: media._id, preview: media.secure_url || img.preview }
            : img
        );
      });
    } catch (err) {
      console.error("Upload failed for entry", entryId, err);
      setPortfolioImages((prev) =>
        prev.map((img) => img.id === entryId ? { ...img, status: "failed", progress: 0 } : img)
      );
    }
  }, []);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const validFiles = files.filter((file) => {
      const fileExt = file.name.split('.').pop().toLowerCase();
      const fileType = file.type || '';

      // HEIC check
      if (fileExt === 'heic' || fileExt === 'heif' || fileType.includes('heic') || fileType.includes('heif')) {
        showError(`${file.name} is in HEIC format. .heic format not supported.`);
        return false;
      }

      const isVid = file.type.startsWith("video/");
      if (!isVid) {
        const isImage = fileType.startsWith('image/') || ['jpg', 'jpeg', 'png'].includes(fileExt);
        if (!isImage || !['jpg', 'jpeg', 'png'].includes(fileExt)) {
          showError("Please upload image under these formats: jpg, jpeg, and png.");
          return false;
        }
      } else {
        if (!ALLOWED_FORMATS.includes(file.type)) {
          showError(`${file.name} is not supported. Only MP4, MOV, WebM videos are allowed.`);
          return false;
        }
      }
      if (isVid && file.size > 100 * 1024 * 1024) {
        showError(`${file.name} is too large. Maximum video size is 100MB.`);
        return false;
      }
      if (!isVid && file.size > 10 * 1024 * 1024) {
        showError(`${file.name} is too large. Maximum image size is 10MB.`);
        return false;
      }
      return true;
    });

    const currentImages = portfolioImages.filter(x => !x.isVid).length;
    const currentVideos = portfolioImages.filter(x => x.isVid).length;
    const incomingImages = validFiles.filter(f => !f.type.startsWith("video/")).length;
    const incomingVideos = validFiles.filter(f => f.type.startsWith("video/")).length;

    if (currentImages + incomingImages > 10) {
      showError(`Maximum 10 images allowed in portfolio. You currently have ${currentImages}.`);
      return;
    }
    if (currentVideos + incomingVideos > 5) {
      showError(`Maximum 5 videos allowed in portfolio. You currently have ${currentVideos}.`);
      return;
    }

    const newEntries = validFiles.map((file) => ({
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      file,
      preview: URL.createObjectURL(file),
      isVid: file.type.startsWith("video/"),
      status: "uploading",
      progress: 0,
      mediaId: null,
    }));

    setPortfolioImages((prev) => [...prev, ...newEntries]);
    newEntries.forEach((entry) => uploadFile(entry.id, entry.file));
  };

  const retryUpload = (entry) => {
    setPortfolioImages((prev) =>
      prev.map((img) => img.id === entry.id ? { ...img, status: "uploading", progress: 0 } : img)
    );
    uploadFile(entry.id, entry.file);
  };

  const removeImage = (entry) => {
    setPortfolioImages((prev) => prev.filter((img) => img.id !== entry.id));
    if (entry.preview?.startsWith("blob:")) URL.revokeObjectURL(entry.preview);
    if (entry.status === "uploaded" && entry.mediaId) {
      MediaUploadService.deleteMedia(entry.mediaId);
    }
  };

  const allUploaded = portfolioImages.every((img) => img.status === "uploaded");
  const hasFailed = portfolioImages.some((img) => img.status === "failed");
  const hasEnough = portfolioImages.filter((img) => img.status === "uploaded").length >= MIN_IMAGES;

  const toggleLanguage = (lang) => {
    if (languages.includes(lang)) {
      setLanguages(languages.filter((l) => l !== lang));
    } else {
      setLanguages([...languages, lang]);
    }
  };

  const handleAddCustomLanguage = (e) => {
    e.preventDefault();
    const lang = newLanguage.trim();
    if (!lang) return;
    if (languages.includes(lang) || predefinedLanguages.includes(lang)) {
      setAddingLanguage(false);
      setNewLanguage("");
      return;
    }
    setLanguages([...languages, lang]);
    setAddingLanguage(false);
    setNewLanguage("");
  };

  const MIN_CATEGORIES = 1;
  const MAX_CATEGORIES = 5;

  const toggleCategory = (cat) => {
    const isCustomObj = typeof cat === "object" && cat !== null && cat.isCustom;
    const catName = isCustomObj ? cat.name : (typeof cat === "string" ? cat : cat.name);
    const catId = isCustomObj ? cat._id : (typeof cat === "string" ? cat : cat._id);

    setSelectedCategories((prev) => {
      const exists = prev.some(c => {
        const cName = typeof c === "string" ? c : c.name;
        const cId = typeof c === "string" ? c : c._id;
        return cId === catId || cName === catName;
      });
      if (exists) {
        return prev.filter(c => {
          const cName = typeof c === "string" ? c : c.name;
          const cId = typeof c === "string" ? c : c._id;
          return cId !== catId && cName !== catName;
        });
      } else {
        const totalSelected = prev.length + selectedSubCategories.length;
        if (totalSelected >= MAX_CATEGORIES) {
          showError(`You can select a maximum of ${MAX_CATEGORIES} work categories.`);
          return prev;
        }
        return [...prev, cat];
      }
    });
  };

  const handleCreateCategorySuggestion = (word) => {
    if (!word || !word.trim()) return;
    const name = word.trim();
    if (name.replace(/\s+/g, ' ').split(' ').length > 3) {
      showError("Suggestion can be at most 3 words.");
      return;
    }
    if (name.length > 35) {
      showError("Suggestion can be at most 35 characters.");
      return;
    }
    
    // Add to selectedCategories
    setSelectedCategories((prev) => {
      const exists = prev.some(e => (typeof e === "string" ? e === name : e.name === name));
      if (exists) return prev;
      
      const totalSelected = prev.length + selectedSubCategories.length;
      if (totalSelected >= MAX_CATEGORIES) {
        showError(`You can select a maximum of ${MAX_CATEGORIES} work categories.`);
        return prev;
      }
      return [...prev, name];
    });

    setCategorySearch("");
    setIsPopupOpen(false);
  };

  const handleCombinedCategoryPopupToggle = (item) => {
    const isSub = typeof item === 'object' && item !== null ? !!item.isSubcategory : false;
    if (isSub) {
      toggleSubCategory(item);
    } else {
      toggleCategory(item);
    }
  };

  const handleContinue = async () => {
    const uploadedCount = portfolioImages.filter((img) => img.status === "uploaded").length;
    const hasEnough = uploadedCount >= MIN_IMAGES;
    const hasFailed = portfolioImages.some((img) => img.status === "failed");

    const totalSelected = selectedCategories.length + selectedSubCategories.length;

    if (languages.length === 0) {
      return showError("Please select at least 1 language.");
    }
    if (totalSelected < MIN_CATEGORIES) {
      return showError(`Please select at least ${MIN_CATEGORIES} work categories (currently ${totalSelected} selected).`);
    }
    if (totalSelected > MAX_CATEGORIES) {
      return showError(`You can select a maximum of ${MAX_CATEGORIES} work categories.`);
    }
    if (!hasEnough) {
      return showError(`Please upload at least ${MIN_IMAGES} portfolio files (currently ${uploadedCount} uploaded).`);
    }
    if (hasFailed) {
      return showError("Some file uploads failed. Please retry or remove them.");
    }

    setIsSubmitting(true);
    const profileData = {
      profession: profession.current,
      languages: languages,
      categories: selectedCategories.map(c => typeof c === "object" && c !== null ? c.name : c),
      rawCategories: selectedCategories,
      subCategories: selectedSubCategories.map(c => typeof c === "object" && c !== null ? c.name : c),
      rawSubCategories: selectedSubCategories,
      portfolioMedia: portfolioImages
        .filter((img) => img.status === "uploaded")
        .map((img) => ({ title: (img.file?.name || "Portfolio").slice(0, 200), media: img.mediaId, url: img.preview, isVid: img.isVid })),
    };

    if (onNext) {
      try {
        profileData.rawExpertise = initialData.rawExpertise || initialData.expertise || [];
        profileData.rawSubExpertise = initialData.rawSubExpertise || initialData.subExpertise || [];
        await onNext(profileData);
      } catch {
        // error shown via toast
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const [selectedSubCategories, setSelectedSubCategories] = useState(initialData.rawSubCategories || initialData.subCategories || []);

  const toggleSubCategory = (subCat) => {
    const isCustomObj = typeof subCat === "object" && subCat !== null && subCat.isCustom;
    const subCatName = isCustomObj ? subCat.name : (typeof subCat === "string" ? subCat : subCat.name);
    const subCatId = isCustomObj ? subCat._id : (typeof subCat === "string" ? subCat : subCat._id);

    setSelectedSubCategories((prev) => {
      const exists = prev.some(e => {
        const eName = typeof e === "string" ? e : e.name;
        const eId = typeof e === "string" ? e : e._id;
        return eId === subCatId || eName === subCatName;
      });
      if (exists) {
        return prev.filter(e => {
          const eName = typeof e === "string" ? e : e.name;
          const eId = typeof e === "string" ? e : e._id;
          return eId !== subCatId && eName !== subCatName;
        });
      } else {
        const totalSelected = selectedCategories.length + prev.length;
        if (totalSelected >= MAX_CATEGORIES) {
          showError(`You can select a maximum of ${MAX_CATEGORIES} work categories.`);
          return prev;
        }
        return [...prev, subCat];
      }
    });
  };



  const isUploading = portfolioImages.some((img) => img.status === "uploading");
  const continueDisabled = isSubmitting || isUploading || hasFailed || !hasEnough;

  const defaultCategories = useMemo(() => {
    const list = [
      "Wedding",
      "Corporate Event",
      "Exhibitions & Trade Shows",
      "Brand Activation",
      "Social Celebrations",
      "Concerts & Live Entertainment"
    ];
    return list.map(name => {
      const found = categoryOptions.find(c => c.name === name);
      if (found) return found;
      return { _id: name, name: name, isDefaultPlaceholder: true };
    });
  }, [categoryOptions]);

  const visibleCategories = useMemo(() => {
    const allSelected = [...selectedCategories, ...selectedSubCategories];
    const baseList = [...defaultCategories];

    // Find custom selected items that are not in the default list
    const selectedCustomItems = allSelected.filter(sel => {
      const selName = typeof sel === 'object' && sel !== null ? sel.name : sel;
      return !baseList.some(d => d.name === selName);
    }).map(sel => {
      if (typeof sel === 'object' && sel !== null) return sel;
      return { _id: sel, name: sel };
    });

    const selectedList = baseList.filter(c =>
      allSelected.some(s => {
        const sName = typeof s === 'object' && s !== null ? s.name : s;
        const cName = c.name || c;
        const sId = typeof s === 'object' && s !== null ? s._id : s;
        const cId = c._id || c;
        return (sId && cId && String(sId) === String(cId)) || String(sName).toLowerCase() === String(cName).toLowerCase();
      })
    );
    const unselectedList = baseList.filter(c =>
      !allSelected.some(s => {
        const sName = typeof s === 'object' && s !== null ? s.name : s;
        const cName = c.name || c;
        const sId = typeof s === 'object' && s !== null ? s._id : s;
        const cId = c._id || c;
        return (sId && cId && String(sId) === String(cId)) || String(sName).toLowerCase() === String(cName).toLowerCase();
      })
    );

    let filteredUnselectedList = unselectedList;
    if (categorySearch) {
      const query = categorySearch.trim().toLowerCase();
      filteredUnselectedList = unselectedList.filter(c => {
        const name = c.name || "";
        return name.toLowerCase().includes(query);
      });
    }

    const countToShow = Math.max(6, selectedList.length + selectedCustomItems.length);
    const combined = [...selectedList, ...selectedCustomItems, ...filteredUnselectedList];

    if (categorySearch) {
      return combined;
    }
    return combined.slice(0, countToShow);
  }, [defaultCategories, selectedCategories, selectedSubCategories, categorySearch]);

  const selectedNamesLower = new Set(
    [...selectedCategories, ...selectedSubCategories].map(c => {
      const name = typeof c === 'object' && c !== null ? c.name : c;
      return String(name || "").trim().toLowerCase();
    })
  );

  const getTagName = (item) => {
    if (!item) return "";
    return typeof item === 'object' ? item.name || item.title || item.label || "" : String(item);
  };

  const selectedCatIds = selectedCategories
    .map(c => typeof c === 'object' && c !== null ? c._id : categoryOptions.find(opt => opt.name === c)?._id)
    .filter(Boolean);

  const query = categorySearch.trim().toLowerCase();

  const filteredCategoryOptions = categoryOptions
    .filter(cat => !cat.isSubcategory)
    .filter(cat => !selectedNamesLower.has(getTagName(cat).toLowerCase()))
    .filter(cat => {
      const name = getTagName(cat);
      if (!query) return true;
      return name.toLowerCase().includes(query);
    });

  const filteredSubCategoryOptions = categoryOptions
    .filter(cat => cat.isSubcategory && cat.parentIds && cat.parentIds.some(parentId => 
      selectedCatIds.some(selId => String(selId) === String(parentId))
    ))
    .filter(cat => !selectedNamesLower.has(getTagName(cat).toLowerCase()))
    .filter(cat => {
      const name = getTagName(cat);
      if (!query) return true;
      return name.toLowerCase().includes(query);
    });

  const combinedCategoryPopupOptions = [...filteredCategoryOptions, ...filteredSubCategoryOptions];

  return (
    <ProfessionalRegistrationLayout
      title={initialData.accountType === "company" ? "Tell Me About Your Business" : "Tell Me About Yourself"}
      description="Help us understand your services so we can connect you with the right clients."
      currentRole={profession.current}
      isRoleFrozen={true}
      onBack={() => {
        onPrev({
          rawCategories: selectedCategories,
          languages,
          portfolioMedia: portfolioImages.map((img) => ({ url: img.preview, media: img.mediaId, isVid: img.isVid })),
        });
      }}
      onContinue={handleContinue}
      continueLabel={isSubmitting ? "Saving..." : isUploading ? "Uploading..." : hasFailed ? "Fix failed uploads" : "Continue"}
      isLoading={isSubmitting || isUploading}
      isDisabled={continueDisabled}
    >
      <div className="flex flex-col gap-5">

        {/* Portfolio Section */}
        <div>
          <h3 className="font-bold text-sm mb-0.5">Portfolio Gallery</h3>
          <p className="text-xs text-gray-400 mb-2">Upload photos or videos.</p>

          <div className="flex gap-3 overflow-x-auto pt-2 pr-2 pb-1" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {portfolioImages.map((img) => (
              <div key={img.id} className="relative flex-shrink-0 w-20 h-20">
                {img.isVid ? (
                  <video src={img.preview} muted preload="metadata" className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <img src={img.preview} alt="" className="w-full h-full object-cover rounded-lg" />
                )}
                {img.status === "uploading" && (
                  <div className="absolute inset-0 bg-black/50 rounded-lg flex flex-col items-center justify-center gap-1">
                    <Spinner />
                    <span className="text-white text-[10px]">{img.progress}%</span>
                  </div>
                )}
                {img.status === "failed" && (
                  <div className="absolute inset-0 bg-red-900/60 rounded-lg flex flex-col items-center justify-center gap-1">
                    <button type="button" disabled={isSubmitting} onClick={() => retryUpload(img)} className="flex flex-col items-center gap-0.5" title="Retry upload">
                      <RotateCcw size={16} className="text-white" />
                      <span className="text-white text-[9px]">Retry</span>
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => removeImage(img)}
                  className="absolute -top-2 -right-2 z-10 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 transition-all shadow-md"
                >
                  <X size={11} />
                </button>
              </div>
            ))}
            {portfolioImages.length < MAX_IMAGES && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
                className="flex-shrink-0 w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-[#FF4C18] transition-colors group"
              >
                <Plus className="w-7 h-7 text-[#FF4C18] group-hover:scale-110 transition-transform" />
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".png,.jpeg,.jpg,.mp4,.mov,.webm,image/png,image/jpeg,image/jpg,video/mp4,video/quicktime,video/webm"
              hidden
              disabled={isSubmitting}
              onChange={handleImageSelect}
            />
          </div>
        </div>

        {/* Languages Section */}
        <div>
          <h3 className="font-bold text-sm mb-2">Languages</h3>
          <div className="flex flex-wrap gap-2">
            {[...new Set([...predefinedLanguages, ...languages])].map((lang) => {
              const isSelected = languages.includes(lang);
              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => toggleLanguage(lang)}
                  className={`px-4 py-2 text-sm rounded-lg border transition-all ${isSelected
                      ? "bg-[#FF4C18] text-white border-[#FF4C18] font-medium"
                      : "bg-white text-gray-600 border-gray-300 hover:border-[#FF4C18] hover:text-[#FF4C18]"
                    }`}
                >
                  {lang}
                </button>
              );
            })}
            {addingLanguage ? (
              <div className="flex items-center bg-white border border-[#FF4C18] rounded-lg px-2 py-1">
                <input
                  type="text"
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddCustomLanguage(e)}
                  onBlur={() => { setAddingLanguage(false); setNewLanguage(""); }}
                  autoFocus
                  className="w-24 outline-none text-sm px-1"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAddingLanguage(true)}
                className="px-4 py-2 text-sm rounded-lg border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 flex items-center gap-1 transition-all"
              >
                <Plus className="w-3.5 h-3.5" />
                Add More
              </button>
            )}
          </div>
        </div>

        {/* Categories Section */}
        <div>
          <h3 className="font-bold text-sm mb-2">Work Categories</h3>
          <div className="flex flex-wrap gap-2 relative" ref={categoryWrapperRef}>
            {visibleCategories.map((cat) => {
              const isSub = typeof cat === 'object' && cat !== null ? !!cat.isSubcategory : false;
              const isActuallySub = isSub || selectedSubCategories.some(c => typeof c === 'object' && c !== null ? c._id === cat._id || c.name === cat.name : c === cat._id || c === cat.name);
              
              const isSelected = 
                selectedCategories.some(c => typeof c === 'object' && c !== null ? c._id === cat._id || c.name === cat.name : c === cat._id || c === cat.name) ||
                selectedSubCategories.some(c => typeof c === 'object' && c !== null ? c._id === cat._id || c.name === cat.name : c === cat._id || c === cat.name);

              const handleToggle = () => {
                if (isActuallySub) {
                  toggleSubCategory(cat);
                } else {
                  toggleCategory(cat);
                }
              };

              const totalSelected = selectedCategories.length + selectedSubCategories.length;
              const disabled = isSubmitting || (!isSelected && totalSelected >= MAX_CATEGORIES);

              return (
                 <button
                  key={cat._id}
                  onClick={handleToggle}
                  disabled={disabled}
                  className={`px-4 py-2 rounded-lg border text-sm transition-all whitespace-nowrap flex items-center justify-center gap-2 ${isSelected
                    ? "bg-[#FF4C18] text-white border-[#FF4C18] font-medium"
                    : "bg-white text-gray-700 border-gray-300 hover:border-[#FF4C18] hover:text-[#FF4C18] hover:bg-orange-50/50"
                    } ${isSubmitting || disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {cat.name}
                </button>
              );
            })}
            <div className={`relative flex items-center ${isPopupOpen ? "z-50 w-full basis-full mt-1" : "z-10"}`}>
              {(() => {
                const totalSelected = selectedCategories.length + selectedSubCategories.length;
                const isLimitReached = totalSelected >= MAX_CATEGORIES;
                return (
                  <div className={`px-4 py-2 rounded-lg border border-dashed border-gray-300 transition-all flex items-center justify-center gap-1 ${isSubmitting || isLimitReached ? "opacity-50 cursor-not-allowed" : "hover:border-[#FF4C18] bg-white"}`}>
                    {!categorySearch && <Plus className="w-4 h-4 text-gray-400" />}
                    <input
                      type="text"
                      placeholder={!categorySearch ? "Add More" : ""}
                      value={categorySearch}
                      onChange={(e) => {
                        setCategorySearch(e.target.value);
                        if (!isPopupOpen) setIsPopupOpen(true);
                      }}
                      onClick={() => setIsPopupOpen(true)}
                      disabled={isSubmitting || isLimitReached}
                      className="bg-transparent border-none outline-none text-center text-sm text-gray-700 w-24 placeholder-gray-500 transition-all disabled:bg-transparent"
                    />
                  </div>
                );
              })()}

              <CategoryPickerPopup
                variant="dropdown"
                isOpen={isPopupOpen}
                onClose={() => setIsPopupOpen(false)}
                options={combinedCategoryPopupOptions}
                selectedIds={[
                  ...selectedCategories.map(c => typeof c === 'object' ? c._id : c),
                  ...selectedSubCategories.map(c => typeof c === 'object' ? c._id : c)
                ]}
                selectedNames={[
                  ...selectedCategories.map(c => typeof c === 'object' && c !== null ? c.name : String(c)),
                  ...selectedSubCategories.map(c => typeof c === 'object' && c !== null ? c.name : String(c))
                ]}
                onToggle={handleCombinedCategoryPopupToggle}
                title="Select Categories"
                searchTerm={categorySearch}
                onCreateSuggestion={handleCreateCategorySuggestion}
              />
            </div>
          </div>
        </div>
      </div>
    </ProfessionalRegistrationLayout>
  );
}
